import type { FormikHelpers } from "formik";
import type { ModuleFull } from "./grasslandTypes";
import { Formik } from "formik";
import { useContext, useEffect, useMemo, useState } from "react";
import {
	initialValues,
	t2InitialValues,
	validationSchema,
	getSchemas,
} from "./grasslandManagement";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import {
	useContextAwareGetGrasslandsDefaults,
	useContextAwareGetGrasslands,
	useUpdateGrasslandsMutation,
} from "@hooks/index";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";
import useModuleHook from "../useModuleHook";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import useErrorMessage from "@utils/useErrorMessage";
import ModuleContext from "../../ModuleContext";

const GrasslandManagementModule = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { belongsToLuc } = useContext(ModuleContext);

	const id = moduleList.find(({ id }) => id === FEModules.Grassland)?.uniqueId;

	const {
		data: grasslandData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetGrasslands(id ?? 0, { refetchOnMountOrArgChange: true });
	const [
		updateGrassland,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateGrasslandsMutation();

	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetGrasslandsDefaults(grasslandData?.module.id ?? 0, { skip: !grasslandData?.module.id });

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});

	const { tabsOpen, errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (grasslandData) {
			setInitValues({ module: grasslandData.module, tiertwo: { ...grasslandData.tiertwo, ...defaults }, });
			processModuleThreads({
				module: grasslandData.module,
			})
		}
	}, [grasslandData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = { data: { ...values, module }, activityId: activeActivityId ?? 0 };

		try {
			values.module.id && await updateGrassland(payload).unwrap();
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema(belongsToLuc)}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.grassland_management"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.Grassland}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				{!tabsOpen ? (
					<GenericTierTwo
						title="project.grassland_management"
						t2Schema={t2Schema}
						tabsOpen={tabsOpen}
						warningMessage={defaultsErrorMsg ?? null}
					/>
				) : null}
			</>
		</Formik>
	);
};

export default GrasslandManagementModule;
